'use client';

import { useMemo } from 'react';

import { useConfig } from '@/react/hooks/useConfig';
import { useLocale } from '@/react/hooks/useLocale';

/**
 * Reads Artlabs integration settings for the current brand/locale and builds the
 * `ArtlabsExperience.init` payload plus the SDK script URL.
 * @returns A 3-tuple: SDK script URL, init options for `ArtlabsExperience.init`, and whether Artlabs is enabled.
 */
export function useArtlabsConfig(): [
  cdnUrl: string,
  initConfig: {
    token: string;
    config: {
      locale: string;
      vto: { logoUrl: string; onboarding: boolean };
    };
  },
  enabled: boolean
] {
  const { language, country } = useLocale();
  const config = useConfig('artlabs');

  const cdnUrl = config.getSetting('script').value;
  const enabled: boolean = config.getSetting('enabled').value;
  const token = config.getSetting('token').value;
  const logoUrl = config.getSetting('logoUrl').value;
  const onboarding = config.getSetting('onboarding').value;
  const locale = `${language}-${country}`;

  const initConfig = useMemo(() => {
    return { token, config: { locale, vto: { logoUrl, onboarding } } };
  }, [token, locale, logoUrl, onboarding]);

  return [cdnUrl, initConfig, enabled];
}
